// ── Workspace panel: pure presentation logic (testable, no DOM/api deps) ──
import type {
  WorkspaceInvitationPublic,
  WorkspaceInvitationStatus,
  WorkspaceMemberRole,
  WorkspacePermissions,
} from './types';

export interface RoleIconPresentation {
  icon: string;
  colorVar: string;
}

// role -> icon + color mapping. owner is visually distinguished (shield_person + accent);
// admin gets a slightly elevated icon; member/guest are plain.
export function roleIcon(role: WorkspaceMemberRole): RoleIconPresentation {
  switch (role) {
    case 'owner':
      return { icon: 'shield_person', colorVar: 'var(--accent)' };
    case 'admin':
      return { icon: 'shield', colorVar: 'var(--accent)' };
    case 'guest':
      return { icon: 'person_outline', colorVar: 'var(--text-muted)' };
    case 'member':
    default:
      return { icon: 'person', colorVar: 'var(--text-muted)' };
  }
}

export interface InvitationStatusPresentation {
  icon: string;
  colorVar: string;
  messageKey: string;
}

export function invitationStatusPresentation(invitation: Pick<WorkspaceInvitationPublic, 'status'>): InvitationStatusPresentation {
  return invitationStatusPresentationForStatus(invitation.status);
}

export function invitationStatusPresentationForStatus(status: WorkspaceInvitationStatus): InvitationStatusPresentation {
  switch (status) {
    case 'pending':
      return { icon: 'schedule', colorVar: 'var(--warning-text)', messageKey: 'workspaceInviteStatusPending' };
    case 'consumed':
      return { icon: 'check_circle', colorVar: 'var(--success)', messageKey: 'workspaceInviteStatusConsumed' };
    case 'revoked':
      return { icon: 'block', colorVar: 'var(--danger)', messageKey: 'workspaceInviteStatusRevoked' };
    case 'expired':
      return { icon: 'error', colorVar: 'var(--danger)', messageKey: 'workspaceInviteStatusExpired' };
    default:
      return { icon: 'help', colorVar: 'var(--text-muted)', messageKey: 'workspaceInviteStatusUnknown' };
  }
}

// Admin-only controls (invite form / role select / remove button / invitations section)
// must not render at all for non-managers — not merely be disabled.
export function canShowAdminControls(permissions: Pick<WorkspacePermissions, 'can_manage_members'> | null | undefined): boolean {
  return Boolean(permissions?.can_manage_members);
}

export function canShowAdminProfiles(permissions: Pick<WorkspacePermissions, 'can_view_admin_profiles'> | null | undefined): boolean {
  return Boolean(permissions?.can_view_admin_profiles);
}

const ERROR_CODE_TO_MESSAGE_KEY: Record<string, string> = {
  membership_missing: 'workspaceErrorMembershipMissing',
  multiple_memberships_detected: 'workspaceErrorMultipleMemberships',
  workspace_not_found: 'workspaceErrorNotFound',
  workspace_admin_forbidden: 'workspaceErrorAdminForbidden',
  member_not_found: 'workspaceErrorMemberNotFound',
  last_owner_protection_triggered: 'workspaceErrorLastOwnerProtected',
  invalid_role: 'workspaceErrorInvalidRole',
  invalid_email: 'workspaceErrorInvalidEmail',
  invalid_invitation_id: 'workspaceErrorInvalidInvitation',
  invitation_not_found: 'workspaceErrorInvitationNotFound',
  invitation_conflict: 'workspaceErrorInvitationConflict',
  invite_target_workspace_conflict: 'workspaceErrorInviteTargetConflict',
  invite_target_identity_ambiguous: 'workspaceErrorInviteTargetAmbiguous',
  invitation_revoked: 'workspaceErrorInvitationRevoked',
  invitation_consumed: 'workspaceErrorInvitationConsumed',
  invitation_expired: 'workspaceErrorInvitationExpired',
  invitation_email_mismatch: 'workspaceErrorInvitationEmailMismatch',
  active_membership_conflict: 'workspaceErrorActiveMembershipConflict',
  migration_conflict: 'workspaceErrorMigrationConflict',
  invalid_acceptance_mode: 'workspaceErrorInvalidAcceptanceMode',
};

const GENERIC_ERROR_MESSAGE_KEY = 'workspaceErrorGeneric';

// Maps a backend error `code` to an i18n key. Unknown/missing codes fall back to
// a generic message key so we never leak raw technical details to the user.
export function errorCodeToMessageKey(code: string | null | undefined): string {
  if (!code) return GENERIC_ERROR_MESSAGE_KEY;
  return ERROR_CODE_TO_MESSAGE_KEY[code] ?? GENERIC_ERROR_MESSAGE_KEY;
}

// display_name fallback for member rows without a resolved profile name yet.
export function memberDisplayName(displayName: string | null | undefined, fallbackKey: string, t: (key: string) => string): string {
  const trimmed = displayName?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : t(fallbackKey);
}
